export type OrderAction = 'edit_items' | 'add_item' | 'remove_item' | 'cancel' | 'delete';

/**
 * Enige UI-gate voor office-order-management Fase 1. Geen status-restricties:
 * office moet altijd kunnen ingrijpen om escalaties op te lossen. Alleen
 * RecordType-gate om te voorkomen dat de edit-UI verschijnt op SR, Webshop of
 * SofaSaver orders (die hebben een ander datamodel).
 *
 * Uitzondering `delete`: een dubbele of foutieve order moet op élk ordertype weg
 * kunnen, en verwijderen raakt het item-datamodel niet — het gooit de hele
 * boom weg. Webshop doet daarom wel mee; de blocker-beslissing zelf ligt
 * server-side (`evalueerDeleteBlockers`), niet hier.
 */
const DELETE_RECORD_TYPES = new Set(['Wasserij', 'Webshop']);

export function canPerform(action: OrderAction, order: { recordType: string }): boolean {
  if (action === 'delete') return DELETE_RECORD_TYPES.has(order.recordType);
  return order.recordType === 'Wasserij';
}

/**
 * Mogen de ophaal-/aflever-opmerkingen bewerkt worden? Dit staat LOS van
 * `canPerform('edit_items')`: `Opmerkingen_Ophalen__c` en
 * `Opmerkingen_Aflevering__c` zijn generieke, informatieve Werk_Order__c-velden
 * die op élk record-type identiek bestaan en niets met het item-datamodel te
 * maken hebben. Ze zijn dus veilig te bewerken op alle order-types (Wasserij,
 * SofaSaver, SR, Webshop). Bij SofaSaver zijn ophalen/afleveren omgedraaid
 * (eerst bezorgen, dan retour ophalen); de kaartlabels vangen dat al af.
 */
export function canEditNotes(_order: { recordType: string }): boolean {
  return true;
}

/**
 * Mag een nieuwe klacht aan deze WO worden gehangen? Geannuleerde orders zijn
 * uitgesloten (geen toegevoegde waarde). Vervolg-WO's mogen wel een eigen
 * klacht krijgen — de klant kan over een vervolg óók klagen.
 *
 * NB: of er al een actieve klacht op de WO hangt, wordt apart afgevangen op
 * basis van `klachtParentId` in de view zelf — dan wordt de knop verborgen.
 */
export function canRegisterKlacht(order: {
  status: string;
  recordType: string;
}): boolean {
  if (order.status === 'Geannuleerd') return false;
  // Voor Fase 1b alleen Wasserij-orders ondersteund. SR/Webshop/SofaSaver volgen later.
  return order.recordType === 'Wasserij';
}

/**
 * Codes moeten 1-op-1 matchen met wat Apex `OrderMutationService.cancelOrder`
 * accepteert (zie comment bovenaan die class).
 */
export interface CancelReason {
  code: string;
  label: string;
}

export const CANCEL_REASONS: ReadonlyArray<CancelReason> = [
  { code: 'klant_geen_interesse', label: 'Klant wil niet meer' },
  { code: 'foute_order',          label: 'Foute order aangemaakt' },
  { code: 'dubbele_order',        label: 'Dubbele order' },
  { code: 'klacht_coulance',      label: 'Klacht — coulance' },
  { code: 'route_onmogelijk',     label: 'Route niet mogelijk' },
  { code: 'anders',               label: 'Anders (vrije tekst)' },
];

/**
 * Codes/labels die naar Apex `WerkOrderDeletionService.VALID_REDENEN` mappen.
 * Het code-veld is de exacte string die de service accepteert. Verschilt van
 * CANCEL_REASONS: delete = "had nooit moeten bestaan", cancel = "klant zag ervan af".
 */
export interface DeleteReason {
  code: 'Dubbele order' | 'Foute klant' | 'Testorder' | 'Anders';
  label: string;
}

export const DELETE_REASONS: ReadonlyArray<DeleteReason> = [
  { code: 'Dubbele order', label: 'Dubbele order' },
  { code: 'Foute klant',   label: 'Verkeerde klant' },
  { code: 'Testorder',     label: 'Testorder' },
  { code: 'Anders',        label: 'Anders (vrije tekst)' },
];

/**
 * Primary-producten voor wasserij-orders. De UI laat de office-medewerker
 * kiezen welk primary-product een nieuwe kleed-rij krijgt; Apex valideert de
 * selectie tegen een harde allowlist (`OrderMutationService.ALLOWED_PRIMARY_SKUS`).
 */
export interface PrimaryOption {
  sku: string;
  label: string;
}

export const PRIMARY_SKU_REINIGEN = 'WAS-PRIM-VLOERKLEED-REINIGEN';
export const PRIMARY_SKU_WEDERVERKOPER = 'WAS-PRIM-VLOERKLEED-WEDERVERKOPER';
export const PRIMARY_SKU_KUSSEN = 'WAS-PRIM-KUSSEN-REINIGEN';

export const PRIMARY_OPTIONS: ReadonlyArray<PrimaryOption> = [
  { sku: PRIMARY_SKU_REINIGEN,      label: 'Vloerkleed' },
  { sku: PRIMARY_SKU_WEDERVERKOPER, label: 'Vloerkleed Wederverkoper' },
  { sku: PRIMARY_SKU_KUSSEN,        label: 'Kussen' },
];

/**
 * Per-stuk primaries (Kussen): geen maten/vorm, geen pakket, prijs per stuk uit
 * Prijsmanagement × Aantal__c. Moet 1-op-1 matchen met Apex
 * `OrderMutationService.PER_STUK_PRIMARY_SKUS`. Zulke items zijn na aanmaken
 * niet bewerkbaar via de edit-dialog (Apex weigert met PER_STUK_NOT_EDITABLE);
 * aantal wijzigen = verwijderen + opnieuw toevoegen.
 */
const PER_STUK_PRIMARY_SKUS: ReadonlySet<string> = new Set([PRIMARY_SKU_KUSSEN]);

export function isPerStukPrimary(sku: string | null | undefined): boolean {
  return !!sku && PER_STUK_PRIMARY_SKUS.has(sku);
}

export function getPrimaryLabel(primarySku: string): string {
  return PRIMARY_OPTIONS.find((p) => p.sku === primarySku)?.label ?? 'Vloerkleed';
}

/**
 * Packages die de office-app in de UI kan selecteren. Deze SKU's moeten
 * bestaan als `Product2.ProductCode` met `Product_Category__c = 'Package'`.
 * Single source of truth voor prijzen blijft Salesforce `Prijsmanagement__c`.
 */
export interface PackageOption {
  sku: string;
  label: string;
}

const REINIGEN_PACKAGES: ReadonlyArray<PackageOption> = [
  { sku: 'WAS-PACK-VLOERKLEED-BASIS',   label: 'Basis Reiniging' },
  { sku: 'WAS-PACK-VLOERKLEED-PROTECT', label: 'Reiniging & Protect' },
  { sku: 'WAS-PACK-VLOERKLEED-PET',     label: 'Huisdier Compleet' },
  { sku: 'WAS-PACK-VLOERKLEED-PREMIUM', label: 'Premium Plus' },
];

const WEDERVERKOPER_PACKAGES: ReadonlyArray<PackageOption> = [
  { sku: 'WAS-PACK-VLOERKLEEDWEDER-BASIS',   label: 'Wederverkoper Basis' },
  { sku: 'WAS-PACK-VLOERKLEEDWEDER-PROTECT', label: 'Wederverkoper Protect' },
];

export const PACKAGE_OPTIONS_BY_PRIMARY: Record<string, ReadonlyArray<PackageOption>> = {
  [PRIMARY_SKU_REINIGEN]: REINIGEN_PACKAGES,
  [PRIMARY_SKU_WEDERVERKOPER]: WEDERVERKOPER_PACKAGES,
};

export function getPackagesForPrimary(primarySku: string): ReadonlyArray<PackageOption> {
  if (isPerStukPrimary(primarySku)) return [];
  return PACKAGE_OPTIONS_BY_PRIMARY[primarySku] ?? REINIGEN_PACKAGES;
}

/**
 * Pakket-children dragen een ProductCode met deze prefix (`WAS-PACK-VLOERKLEED-*`
 * en `WAS-PACK-VLOERKLEEDWEDER-*`). Een pakket-regel kan niet los verwijderd
 * worden via de extra-remove-flow — Apex weigert dat ("gaat via setPackage").
 * Wisselen gaat via de pakketkeuze in EditWasserijItemDialog.
 */
export function isPackageSku(sku: string | null | undefined): boolean {
  return !!sku && sku.startsWith('WAS-PACK-');
}

export function getDefaultPackageForPrimary(primarySku: string): string | null {
  return getPackagesForPrimary(primarySku)[0]?.sku ?? null;
}

/** @deprecated gebruik `getPackagesForPrimary(PRIMARY_SKU_REINIGEN)` — behouden voor backwards-compat. */
export const PACKAGE_OPTIONS: ReadonlyArray<PackageOption> = REINIGEN_PACKAGES;

/**
 * Maximum additions in één klik via de "Aantal"-input (AddItemForm en
 * AddKleedDialog). Apex verwerkt alle adds gebulkt (2 DML totaal) en de
 * reprice-trigger werkt per werkorder, dus dit aantal is governor-veilig.
 */
export const MAX_BULK_ADD = 40;
